import axios from 'axios';

// Configuración base para el servicio de autenticación
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

const authAPI = axios.create({
  baseURL: `${API_BASE_URL}/auth`,
  timeout: Number(process.env.REACT_APP_API_TIMEOUT) || 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para manejo de errores específicos de auth
authAPI.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('Auth Service Error:', error);

    // Si hay error 401, limpiar token
    if (error.response?.status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
    }

    return Promise.reject(error);
  }
);

// Servicios de autenticación
export const authService = {
  // Login de usuario
  login: async (email, password) => {
    try {
      const response = await authAPI.post('/login', { email, password });

      if (response.data.access_token) {
        localStorage.setItem('authToken', response.data.access_token);

        // Guardar información del usuario si viene en la respuesta
        if (response.data.user) {
          localStorage.setItem('user', JSON.stringify(response.data.user));
        }
      }

      return response.data;
    } catch (error) {
      if (error.response?.status === 403) {
        throw new Error(error.response.data.message || 'Debes confirmar tu email antes de iniciar sesión.');
      }
      if (error.response?.status === 401 || error.response?.status === 404) {
        throw new Error('Email o contraseña incorrectos');
      }
      throw new Error('Error al iniciar sesión. Intenta nuevamente.');
    }
  },

  // Registro de usuario
  register: async (userData) => {
    try {
      const response = await authAPI.post('/register', userData);

      // Auto-login después del registro
      if (response.data.access_token) {
        localStorage.setItem('authToken', response.data.access_token);

        if (response.data.user) {
          localStorage.setItem('user', JSON.stringify(response.data.user));
        }
      }

      return response.data;
    } catch (error) {
      if (error.response?.status === 409) {
        throw new Error('El email ya está registrado');
      }
      if (error.response?.status === 400) {
        throw new Error('Error en los datos proporcionados. Verifica todos los campos.');
      }
      throw new Error('Error al registrar usuario: ' + (error.response?.data?.message || error.message));
    }
  },

  // Login con Google
  googleLogin: async (accessToken) => {
    try {
      const response = await authAPI.post('/google', { accessToken });

      if (response.data.access_token) {
        localStorage.setItem('authToken', response.data.access_token);

        if (response.data.user) {
          localStorage.setItem('user', JSON.stringify(response.data.user));
        }
      }

      return response.data;
    } catch (error) {
      if (error.response?.status === 401) {
        throw new Error('Error de autenticación con Google');
      }
      throw new Error('Error al iniciar sesión con Google: ' + (error.response?.data?.message || error.message));
    }
  },

  // Obtener perfil del usuario autenticado
  getProfile: async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No hay token de autenticación');
      }

      const response = await authAPI.get('/profile', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      // Actualizar información del usuario en localStorage
      localStorage.setItem('user', JSON.stringify(response.data));

      return response.data;
    } catch (error) {
      throw new Error('Error al obtener el perfil: ' + error.message);
    }
  },

  // Actualizar perfil del usuario
  updateProfile: async (userData) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No hay token de autenticación');
      }

      const response = await authAPI.patch('/profile', userData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      // Actualizar información del usuario en localStorage
      localStorage.setItem('user', JSON.stringify(response.data));

      return response.data;
    } catch (error) {
      throw new Error('Error al actualizar el perfil: ' + error.message);
    }
  },

  // Cambiar contraseña
  changePassword: async (currentPassword, newPassword) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No hay token de autenticación');
      }

      const response = await authAPI.patch('/change-password', {
        currentPassword,
        newPassword,
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return response.data;
    } catch (error) {
      if (error.response?.status === 400) {
        throw new Error('Contraseña actual incorrecta');
      }
      throw new Error('Error al cambiar la contraseña: ' + error.message);
    }
  },

  // Reenviar email de confirmación
  resendConfirmation: async (email) => {
    try {
      const response = await authAPI.post('/resend-confirmation', { email });
      return response.data;
    } catch (error) {
      if (error.response?.status === 429) {
        throw new Error(error.response.data.message || 'Esperá un momento antes de reenviar.');
      }
      throw new Error('Error al reenviar el email. Intenta nuevamente.');
    }
  },

  // Solicitar restablecimiento de contraseña
  requestPasswordReset: async (email) => {
    try {
      const response = await authAPI.post('/request-password-reset', { email });
      return response.data;
    } catch (error) {
      if (error.response?.status === 429) {
        throw new Error(error.response.data.message || 'Esperá un momento antes de reenviar.');
      }
      throw new Error('Error al enviar el email. Intenta nuevamente.');
    }
  },

  // Restablecer contraseña con token
  resetPassword: async (token, newPassword) => {
    try {
      const response = await authAPI.post('/reset-password', { token, newPassword });
      return response.data;
    } catch (error) {
      if (error.response?.status === 400) {
        throw new Error(error.response.data.message || 'Token inválido o expirado.');
      }
      throw new Error('Error al restablecer la contraseña. Intenta nuevamente.');
    }
  },

  // Confirmar email
  confirmEmail: async (token) => {
    try {
      const response = await authAPI.get(`/confirm-email/${token}`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 400) {
        throw new Error(error.response.data.message || 'Token inválido o expirado');
      }
      throw new Error('Error al confirmar el email. Intenta nuevamente.');
    }
  },

  // Logout
  logout: () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
  },

  // Verificar si el usuario está autenticado
  isAuthenticated: () => {
    return localStorage.getItem('authToken') !== null;
  },

  // Obtener token de autenticación
  getToken: () => {
    return localStorage.getItem('authToken');
  },

  // Obtener información del usuario desde localStorage
  getUser: () => {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  },

  // Verificar si el usuario tiene un rol específico
  hasRole: (role) => {
    const user = authService.getUser();
    return user?.role === role;
  },

  // Refresh token (si está implementado en el backend)
  refreshToken: async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No hay token de autenticación');
      }

      const response = await authAPI.post('/refresh', {}, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.access_token) {
        localStorage.setItem('authToken', response.data.access_token);
      }

      return response.data;
    } catch (error) {
      // Si el refresh falla, hacer logout
      authService.logout();
      throw new Error('Sesión expirada');
    }
  },
};

export default authService;